import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth/auth-provider";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import {
  fieldDefsForForm,
  getFiling,
  listFilingFields,
  recordFilingExport,
  type FilingFieldRow,
} from "@/lib/api/filings";
import { listFunds } from "@/lib/api/funds";
import {
  FORM_LABEL,
  FUND_TYPE_LABEL,
  REVIEW_LABEL,
  SECTION_LABEL,
  SECTION_ORDER_BY_FORM,
  SOURCE_LABEL,
} from "@/lib/filing-labels";
import { filingCompleteness, type FormPfFieldDef, type FormPfFundType } from "@argus/engine";

/**
 * Filing-ready package — the print-clean Form PF deliverable (Master Plan Argus
 * Phase 3 "→ filing-ready"). Honest by construction: every value is labeled with
 * its provenance (auto / AI / manual) and its review state, and outstanding gaps
 * are listed explicitly. SEC rails (PFRD) do not allow third-party auto-filing —
 * this is the package a CCO reviews and submits. Browser print-to-PDF is the
 * export; a JSON export gives a machine-readable handoff.
 */

function fmtUsd(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${n.toLocaleString("en-US")}`;
}

interface CompositionEntry {
  type: string;
  amount: number;
  pct: number;
}

/** Safe stringify for an `unknown` jsonb value (never "[object Object]"). */
function asText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function display(def: FormPfFieldDef, value: unknown): string {
  if (value === null || value === undefined || value === "") return "— (gap: to be supplied)";
  switch (def.type) {
    case "currency":
      return fmtUsd(Number(value));
    case "percent":
      return `${Number(value)}%`;
    case "boolean":
      return value ? "Yes" : "No";
    case "enum":
      return def.key.includes("fund_type")
        ? FUND_TYPE_LABEL[value as FormPfFundType]
        : asText(value);
    case "composition":
      return (value as CompositionEntry[]).map((c) => `${c.type} ${c.pct}%`).join(", ");
    default:
      return asText(value);
  }
}

function PackageInner({ filingId }: { filingId: string }) {
  const tenant = useTenant();
  const { user } = useAuth();

  const { data: filing } = useQuery({
    queryKey: ["filing", filingId],
    queryFn: () => getFiling(filingId),
  });
  const { data: fields } = useQuery({
    queryKey: ["filing-fields", filingId],
    queryFn: () => listFilingFields(filingId),
  });
  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });

  if (!filing) return <p className="text-sm text-neutral-500">Loading package…</p>;
  const fil = filing; // narrowed const for closures below

  const defs = fieldDefsForForm(fil.form_type, funds ?? []);
  const sectionOrder = SECTION_ORDER_BY_FORM[fil.form_type];
  const fundName = new Map((funds ?? []).map((f) => [f.id, f.short_name ?? f.name]));
  const rows = fields ?? [];
  const completeness = filingCompleteness(
    rows.map((f) => ({
      section: f.section,
      source: f.source,
      value: f.value,
      isGap: f.is_gap,
      reviewStatus: f.review_status,
    })),
  );
  const gaps = rows.filter((f) => f.is_gap && (f.value === null || f.value === ""));

  function exportJson() {
    const payload = {
      form_type: fil.form_type,
      title: fil.title,
      period: fil.period_label,
      fiscal_year_end: fil.fiscal_year_end,
      due_date: fil.due_date,
      status: fil.status,
      engine_version: fil.engine_version,
      generated_at: new Date().toISOString(),
      tenant: tenant.name,
      completeness: {
        populated: completeness.populated,
        total: completeness.total,
        gaps: completeness.gaps,
      },
      fields: rows.map((f) => ({
        field_key: f.field_key,
        label: defs.get(f.field_key)?.label ?? f.field_key,
        section: f.section,
        value: f.value,
        source: f.source,
        is_gap: f.is_gap,
        review_status: f.review_status,
        confidence: f.confidence,
        citation: f.citation,
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fil.form_type}-${fil.period_label ?? "filing"}.json`;
    a.click();
    URL.revokeObjectURL(url);
    if (user) void recordFilingExport(fil, user.id);
  }

  function groupByFund(sectionRows: FilingFieldRow[]): [string, FilingFieldRow[]][] {
    const groups: [string, FilingFieldRow[]][] = [];
    const index = new Map<string, FilingFieldRow[]>();
    for (const r of sectionRows) {
      const fundId = r.field_key.split("__")[1] ?? "—";
      let arr = index.get(fundId);
      if (!arr) {
        arr = [];
        index.set(fundId, arr);
        groups.push([fundId, arr]);
      }
      arr.push(r);
    }
    return groups;
  }

  return (
    <div className="print:text-black">
      <header className="flex items-start justify-between print:hidden">
        <div>
          <Link
            to="/t/$slug/filings/$filingId"
            params={{ slug: tenant.slug, filingId }}
            className="text-xs text-neutral-500 hover:text-neutral-300"
          >
            ← Back to filing
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">Filing-ready package</h1>
          <p className="text-sm text-neutral-400">
            Print-ready {FORM_LABEL[filing.form_type]} package. Use your browser&rsquo;s print
            dialog → &ldquo;Save as PDF&rdquo;, or export JSON for a machine-readable handoff.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportJson}
            className="rounded border border-neutral-700 px-4 py-2 text-sm font-medium text-neutral-200 hover:bg-neutral-800"
          >
            Export JSON
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-950 hover:bg-white"
          >
            Print / Save as PDF
          </button>
        </div>
      </header>

      <article className="mt-4 space-y-6 rounded border border-neutral-800 bg-white p-8 text-neutral-900 print:mt-0 print:rounded-none print:border-0 print:p-0">
        <header className="border-b border-neutral-300 pb-4">
          <h1 className="text-2xl font-bold">
            {FORM_LABEL[filing.form_type]} — Filing-ready package
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            {tenant.name}
            {filing.period_label ? ` · ${filing.period_label}` : ""}
            {filing.due_date ? ` · Due ${filing.due_date}` : ""}
          </p>
          <p className="text-xs text-neutral-500">
            Generated {new Date().toLocaleString()} by Argus ({filing.engine_version}).
            Auto-populated and AI-drafted values are preparation aids; each is labeled with its
            provenance and review state. Argus does not file with the SEC — review and submit via
            PFRD.
          </p>
        </header>

        <section>
          <h2 className="text-lg font-semibold">Summary</h2>
          <table className="mt-2 text-sm">
            <tbody>
              <tr>
                <td className="pr-8 text-neutral-600">Fields populated</td>
                <td className="font-medium">
                  {completeness.populated} / {completeness.total}
                </td>
              </tr>
              <tr>
                <td className="pr-8 text-neutral-600">Human-reviewed</td>
                <td className="font-medium">{completeness.reviewedPct}%</td>
              </tr>
              <tr>
                <td className="pr-8 text-neutral-600">Outstanding gaps</td>
                <td className="font-medium">{completeness.gaps}</td>
              </tr>
              <tr>
                <td className="pr-8 text-neutral-600">Status</td>
                <td className="font-medium capitalize">{filing.status.replace(/_/g, " ")}</td>
              </tr>
            </tbody>
          </table>
        </section>

        {sectionOrder.map((section) => {
          const sectionRows = rows.filter((f) => f.section === section);
          if (sectionRows.length === 0) return null;
          const perFund = sectionRows.some((f) => f.field_key.includes("__"));
          return (
            <section key={section} className="break-inside-avoid">
              <h2 className="text-lg font-semibold">{SECTION_LABEL[section]}</h2>
              {perFund ? (
                groupByFund(sectionRows).map(([fundId, frows]) => (
                  <div key={fundId} className="mt-2">
                    <h3 className="text-sm font-semibold text-neutral-700">
                      {fundName.get(fundId) ?? "Fund"}
                    </h3>
                    <FieldTable rows={frows} defs={defs} />
                  </div>
                ))
              ) : (
                <FieldTable rows={sectionRows} defs={defs} />
              )}
            </section>
          );
        })}

        {gaps.length > 0 && (
          <section className="break-inside-avoid">
            <h2 className="text-lg font-semibold">Outstanding gaps ({gaps.length})</h2>
            <p className="text-xs text-neutral-600">
              Values Argus cannot derive — supply these before filing.
            </p>
            <ul className="mt-2 list-disc pl-5 text-sm">
              {gaps.map((g) => (
                <li key={g.id}>
                  {defs.get(g.field_key)?.label ?? g.field_key}
                  {g.field_key.includes("__")
                    ? ` — ${fundName.get(g.field_key.split("__")[1] ?? "") ?? ""}`
                    : ""}
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </div>
  );
}

function FieldTable({
  rows,
  defs,
}: {
  rows: FilingFieldRow[];
  defs: Map<string, FormPfFieldDef & { fundId: string | null }>;
}) {
  return (
    <table className="mt-1 w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-neutral-300 text-left text-xs uppercase text-neutral-500">
          <th className="py-1 pr-4">Item</th>
          <th className="py-1 pr-4">Field</th>
          <th className="py-1 pr-4">Value</th>
          <th className="py-1 pr-4">Source</th>
          <th className="py-1">Review</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((f) => {
          const def = defs.get(f.field_key);
          if (!def) return null;
          return (
            <tr key={f.id} className="border-b border-neutral-200 align-top">
              <td className="w-24 py-1 pr-4 text-xs text-neutral-500">{def.item}</td>
              <td className="py-1 pr-4 font-medium">{def.label}</td>
              <td className="py-1 pr-4 text-neutral-800">{display(def, f.value)}</td>
              <td className="w-20 py-1 pr-4 text-xs uppercase text-neutral-500">
                {SOURCE_LABEL[f.source]}
              </td>
              <td className="w-24 py-1 text-xs text-neutral-500">
                {REVIEW_LABEL[f.review_status]}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function FilingPackagePage() {
  const { filingId } = useParams({ strict: false });
  return (
    <TenantLayout>
      <PackageInner filingId={filingId ?? ""} />
    </TenantLayout>
  );
}
